import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuArrowRight } from 'react-icons/lu';
import { BELOW_TABLET_MEDIA, BLANK_IMAGE, Scene } from '../../../design-system/components';
import AppStoreBadges from '../components/AppStoreBadges';
import LinkButton from '../components/LinkButton';
import PhoneMockup from '../components/PhoneMockup';
import { trackUserEvent } from '../../../utils/reddit-pixel';

/**
 * The hero (redesign PR10, the approved "Landing" frames). From 768 the
 * wide landing scene sits behind the right of the hero, behind the phone
 * and the long-neck giraffe, never behind the heading or the copy; below
 * 768 the scene's phone crop is a band under the navbar with the peeking
 * giraffe on it, and the phone is not shown. The art is decorative (Scene
 * is aria-hidden).
 *
 * The page is prerendered at 1280, so nothing here reads the width in
 * JavaScript: both variants are in the static HTML and the stylesheet shows
 * one (Scene gate="css", and the desktop visual's display:none below 768 in
 * landing.css), so a phone paints its band from the first frame. Every
 * image is in a <picture> that resolves to a blank where it is hidden, so a
 * phone downloads neither the wide art, the screenshot nor the long-neck
 * giraffe, and a desktop does not download the peeking sprite. The band's
 * sprite is above the fold on a phone, so it loads eagerly. The copy, links
 * and alt text are the page's search assets and are unchanged
 * (pages/landing/__tests__).
 */
export default function HeroSection() {
  return (
    <section className="ee-landing-hero">
      <Scene section="landing" variant="hero" gate="css" className="ee-landing-hero__scene" />
      <Scene
        section="landing"
        variant="mobile"
        mascot="peek"
        mascotLoading="eager"
        gate="css"
        className="ee-landing-hero__band"
      />
      <div className="ee-landing-container ee-landing-hero__grid">
        <div className="ee-landing-hero__copy">
          <motion.h1
            className="ee-landing-hero__title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Stop Fighting.
            <br />
            <span className="ee-landing-hero__accent">Start Connecting.</span>
          </motion.h1>

          <motion.p
            className="ee-landing-hero__lede"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            Learn the communication skills that save relationships — in just 10 minutes a day.
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="ee-landing-hero__ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <LinkButton
              to="/register"
              size="lg"
              onClick={() => trackUserEvent('Custom', { customEventName: 'ClickStartFree' })}
              trailingIcon={<LuArrowRight />}
            >
              Start Free
            </LinkButton>
            <Link
              to="/quiz-assessment"
              onClick={() => trackUserEvent('Custom', { customEventName: 'ClickQuizCTA' })}
              className="ee-landing-quiz-link"
            >
              <span className="ee-landing-quiz-link__title">Know Your Relationship Superpower</span>
              <span className="ee-landing-quiz-link__sub">3-min communication quiz</span>
            </Link>
          </motion.div>

          {/* App Store badges */}
          <motion.div
            className="ee-landing-hero__stores"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <p className="ee-landing-hero__stores-label">Also available on</p>
            <AppStoreBadges size="sm" className="ee-landing-stores" />
          </motion.div>
        </div>

        {/* The phone with the giraffe peeking from behind it (from 768) */}
        <motion.div
          className="ee-landing-hero__visual"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="ee-landing-hero__phone">
            <div className="ee-landing-hero__device">
              <PhoneMockup
                src="/screenshots/empathease-stats-empathy-impact.webp"
                alt="EmpathEase app showing empathy impact stats, streaks, and community contributions"
                eager
                skipMedia={BELOW_TABLET_MEDIA}
              />
            </div>
            <picture>
              <source media={BELOW_TABLET_MEDIA} srcSet={BLANK_IMAGE} />
              <motion.img
                src="/landing-giraffe.webp"
                alt=""
                aria-hidden="true"
                width="512"
                height="512"
                loading="lazy"
                decoding="async"
                className="ee-landing-hero__giraffe"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              />
            </picture>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
